import * as Actions from '../actions/bankAccountActions';
import BankAccount from '../../entities/bankAccount';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type BankAccountAction = { 
    type: string; 
    bankAccount: BankAccount | null; 
    bankAccountId: string | BankAccount; 
    partial: boolean; 
    lastFetch: number; 
    bankAccounts: BankAccount[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<BankAccount> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {BankAccount[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<BankAccount> | null, firmId: string, userId: string, updates?: BankAccount[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'bankAccount', 'bankAccounts', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<BankAccount>} data
 * @param {BankAccount[]} [updates]
 * @returns {ArrayMap<BankAccount>}
 */
function updateStorage(data: ArrayMap<BankAccount>, updates?: BankAccount[]): ArrayMap<BankAccount> {
	if (logging) console.debug('⮂ bankAccountReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {BankAccount[]} [updates]
 * @returns {ArrayMap<BankAccount>}
 */
function updateStorageNoReturn(updates?: BankAccount[]): void {
	if (logging) console.debug('⮂ bankAccountReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {BankAccount[]} [data]
 * @returns {ArrayMap<BankAccount>}
 */
function createState(data?: BankAccount[] | ArrayMap<BankAccount>): ArrayMap<BankAccount> {
	if (!data) return ArrayMapRegistry.clone<BankAccount>('bankAccount', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<BankAccount>('bankAccount', data.map(e => e));
    else return ArrayMapRegistry.clone<BankAccount>('bankAccount', data);
}

function bankAccounts(state: ArrayMap<BankAccount>, action: BankAccountAction): ArrayMap<BankAccount> {
	if (!state) state = createState();

	let st: ArrayMap<BankAccount>;

	switch (action.type) {
		// action.bankAccount - the new value of bankAccount with id action.id
		// action.bankAccountId - id of the target, used if new value is null
		case Actions.BANKACCOUNT_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ bankAccountReducer: FETCH_ONE_SUCCESS', action);

			if (action.bankAccount === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.bankAccountId)) {
				st = state.clone();
				st.set(action.bankAccount);
				// state.clear();

				updateStorageNoReturn([action.bankAccount]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.bankAccounts
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.bankAccounts
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.bankAccounts
			 !partial, !stateEmpty, fetchEmpty	-> D return bankAccounts in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge bankAccounts in state newer than those in action.bankAccounts
		 */
		case Actions.BANKACCOUNT_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.bankAccounts.length === 0;
			const fetched = action.bankAccounts.map(bankAccount => new BankAccount({...bankAccount, persisted: true}));

			if (logging) console.debug(`⮂ bankAccountReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} bankAccounts with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ bankAccountReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ bankAccountReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<BankAccount> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: BankAccount[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(bankAccount => {
					const current = st.get(bankAccount.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && bankAccount.status === 'D') {
							// Remove it from state
							toUpdate.push(bankAccount);
							st.delete(bankAccount);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (bankAccount.addedOn > current.addedOn) {
								toUpdate.push(bankAccount);
								st.set(bankAccount);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (bankAccount.status !== 'D') {
							toUpdate.push(bankAccount);
							st.set(bankAccount);
						}
						else if (logging) console.debug('⮂ bankAccountReducer: partialMerge: skipping deleted bankAccount not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ bankAccountReducer: empty: bankAccounts', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ bankAccountReducer: fullMerge: bankAccounts', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localBankAccount => {
                    // If this bankAccount has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localBankAccount.persisted) {
                        const fetchedBankAccount = st.get(localBankAccount.id);
                        if (!fetchedBankAccount || localBankAccount.addedOn > fetchedBankAccount.addedOn) st.set(localBankAccount);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.BANKACCOUNT_FETCH_ONE_FAILURE:
			console.error('BANKACCOUNT_FETCH_ONE_FAILURE', action.bankAccountId, action.err);

			// No update to storage required
			return state;

		case Actions.BANKACCOUNT_FETCH_FAILURE:
			console.error('BANKACCOUNT_FETCH_FAILURE', action.bankAccountId, action.err);

			// No update to storage required
			return state;

		case Actions.BANKACCOUNT_RESTORE: {
		    let bankAccount = new BankAccount({...action.bankAccount});
            st = state.clone();

            const existing = st.get(bankAccount.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (bankAccount.addedOn >= existing.addedOn) {
                    st.set(bankAccount);
                    return updateStorage(st, [bankAccount]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (bankAccount.status === 'D') return state;

                st.set(bankAccount);
                return updateStorage(st, [bankAccount]);
            }
        }
            
		case Actions.BANKACCOUNT_SAVE: {
		    // console.debug('⮂ BANKACCOUNT_SAVE reducer', action.bankAccount?.id);
		    
			// clone the bankAccount
			let bankAccount = new BankAccount({...action.bankAccount});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new bankAccount in the state
			// If this is a create, add to the queue
			const _bankAccount = st.get(bankAccount.id);

			if (!_bankAccount) st.set(bankAccount);
			else {
				if (bankAccount.addedOn >= _bankAccount.addedOn) st.set(bankAccount);
			}

			return updateStorage(st, [bankAccount]);
		}

		case Actions.BANKACCOUNT_SAVE_MANY: {
		    if (logging) console.debug('⮂ BANKACCOUNT_SAVE_MANY reducer', action.bankAccounts.map(bankAccount => bankAccount.id).join(','));
		    
			// clone the bankAccounts
			let toProcess = action.bankAccounts.map(bankAccount => new BankAccount(bankAccount));

			st = state.clone();
			// state.clear();

			const updates: BankAccount[] = [];

			toProcess.forEach(bankAccount => {
				// Add or update the new bankAccount in the state
				// If this is a create, add to the queue
				const existing = st.get(bankAccount.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (bankAccount.status !== 'D') {
						st.push(bankAccount);
						updates.push(bankAccount);
					}
					else if (logging) console.debug('⮂ BANKACCOUNT_SAVE_MANY: skipping deleted bankAccount not in state');
				}
				else {
					if (bankAccount.addedOn >= existing.addedOn) {
						st.set(bankAccount);
						updates.push(bankAccount);
					}
					else if (logging) console.debug('⮂ Skipping, existing bankAccount had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.BANKACCOUNT_REMOVE: {
			// clone the bankAccount
			const bankAccount = new BankAccount({...action.bankAccount});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(bankAccount);

			return updateStorage(st, [bankAccount]);
		}

		case Actions.BANKACCOUNT_RESTORE_FAILURE:
		case Actions.BANKACCOUNT_SAVE_FAILURE: {
		    console.warn('BANKACCOUNT_SAVE_FAILURE reducer', action.bankAccount?.id);
		    if (!action.bankAccount?.id) {
				console.warn('BANKACCOUNT_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const bankAccount = ensure(BankAccount, action.bankAccount);
			if (!bankAccount) {
				console.error('Could not find bankAccount after call to save');
				return state;
			}

			st = state.clone();

			const _bankAccountRef = state.get(bankAccount.id);

			if (_bankAccountRef && (_bankAccountRef.addedOn === bankAccount.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _bankAccount = new BankAccount({..._bankAccountRef});
				_bankAccount.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_bankAccount.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('bankAccount', _bankAccount).then(r => {
						if (r.isErr()) console.error(`⮂ bankAccountReducer: failed to save bankAccount with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ bankAccountReducer: saved bankAccount with fatalError info');
					}).catch(err => console.error(`⮂ bankAccountReducer: failed to save bankAccount with fatalError info: ${err.message}`));

				st.set(_bankAccount);
			}

			return updateStorage(st);
		}

		case Actions.BANKACCOUNT_SAVE_MANY_FAILURE: {
		    console.warn('BANKACCOUNT_SAVE_MANY_FAILURE reducer', action.bankAccounts.map(bankAccount => bankAccount.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.BANKACCOUNT_RESTORE_SUCCESS:
		case Actions.BANKACCOUNT_SAVE_SUCCESS: {
			if (!action.bankAccount?.id) {
				console.warn('BANKACCOUNT_SAVE_SUCCESS reducer');
                return state;
			}

			const bankAccountRef = state.get(action.bankAccount?.id);
			if (!bankAccountRef) {
				console.warn('Could not find bankAccount after call to save');
                return state;
			}

			st = state.clone();

			if (action.bankAccount.addedOn >= bankAccountRef.addedOn) {
				st.set(action.bankAccount);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const bankAccount = new BankAccount({...bankAccountRef, persisted: true});
				st.set(bankAccount);
			}

			return updateStorage(st, [action.bankAccount]);
		}

		case Actions.BANKACCOUNT_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ BANKACCOUNT_SAVE_MANY_SUCCESS reducer', action.bankAccounts.map(bankAccount => bankAccount.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const bankAccounts: BankAccount[] = [];

			action.bankAccounts.forEach(e => {
				const bankAccountRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!bankAccountRef) console.warn('Could not find bankAccount after call to save');
				else if (e.addedOn >= bankAccountRef.addedOn) {
				    st.set(e);
				    bankAccounts.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const bankAccount = new BankAccount({...bankAccountRef, persisted: true});
					st.set(bankAccount);
					bankAccounts.push(bankAccount);
				}
			});

			return updateStorage(st, bankAccounts);
		}

		case Actions.BANKACCOUNT_REMOVE_FAILURE: {
		    console.warn('BANKACCOUNT_REMOVE_FAILURE action', action.bankAccount?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.BANKACCOUNT_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.BANKACCOUNT_RESTORE_MANY: {
			if (logging) console.debug('⮂ BANKACCOUNT_RESTORE_MANY reducer', action.bankAccounts.map(bankAccount => bankAccount.id).join(','));

			const toProcess = action.bankAccounts.map(bankAccount => new BankAccount({...bankAccount}));
			st = state.clone();

			const updates: BankAccount[] = [];

			toProcess.forEach(bankAccount => {
				const existing = st.get(bankAccount.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (bankAccount.addedOn >= existing.addedOn) {
						st.set(bankAccount);
						updates.push(bankAccount);
					}
					else if (logging) console.debug('⮂ BANKACCOUNT_RESTORE_MANY: skipping older version of bankAccount');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (bankAccount.status !== 'D') {
						st.set(bankAccount);
						updates.push(bankAccount);
					}
					else if (logging) console.debug('⮂ BANKACCOUNT_RESTORE_MANY: skipping deleted bankAccount not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.BANKACCOUNT_REMOVE_MANY: {
			if (logging) console.debug('⮂ BANKACCOUNT_REMOVE_MANY reducer', action.bankAccounts.map(bankAccount => bankAccount.id).join(','));

			const toProcess = action.bankAccounts.map(bankAccount => new BankAccount({...bankAccount}));
			st = state.clone();

			toProcess.forEach(bankAccount => {
				st.delete(bankAccount);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default bankAccounts;